import React from 'react';
import { 
  ArrowLeft,
  ArrowRight,
  Server, 
  Globe, 
  Shield, 
  Layers, 
  Code, 
  Check,
  Github,
  PieChart
} from 'lucide-react';

const AwsMigrationCaseStudy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-6 py-16">
          <a href="/projects" className="inline-flex items-center text-blue-100 hover:text-white mb-8">
            <ArrowLeft className="mr-2" size={16} /> Back to Projects
          </a>
          <h1 className="text-4xl font-bold mb-4">Migration from DigitalOcean PaaS to AWS SaaS</h1>
          <div className="flex flex-col md:flex-row md:items-center text-blue-100 mb-6">
            <div className="flex items-center mr-8 mb-2 md:mb-0">
              <Server size={18} className="mr-2" /> 
              <span>Cloud Architecture</span>
            </div>
            <div className="flex items-center mr-8 mb-2 md:mb-0">
              <Globe size={18} className="mr-2" /> 
              <span>Web Development</span>
            </div>
            <div className="flex items-center">
              <Shield size={18} className="mr-2" /> 
              <span>Security</span>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3 bg-blue-50 p-8">
              <h2 className="text-2xl font-bold mb-6 text-blue-900">Project Overview</h2>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Timeline</h3>
                <p className="text-gray-700">May 2020 - December 2020</p>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Role</h3>
                <p className="text-gray-700">Lead Cloud Engineer</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">AWS Lambda</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">API Gateway</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">CloudFront</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">S3</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Cognito</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">CodePipeline</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">CloudFormation</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">AWS SAM</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">React</span>
                </div>
              </div>
            </div>
            <div className="md:w-2/3 p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">The Challenge</h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                The existing application was running on DigitalOcean's Platform as a Service, which became 
                increasingly restrictive as the application scaled. The legacy system faced challenges with:
              </p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Limited scalability during peak usage periods</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Growing operational costs with traditional architecture</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Technical debt in the jQuery-based frontend</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Security concerns with the existing authentication system</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Manual deployment processes with limited automation</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The project goal was to completely redesign the architecture as a serverless application on AWS, 
                improving scalability, reducing costs, enhancing security, and modernizing the frontend for 
                better maintainability.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Section */}
      <div className="container mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">The Solution</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Server className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Backend Architecture</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented serverless API with AWS Lambda and API Gateway</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Secured API endpoints with AWS Cognito authentication</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Defined infrastructure as code using AWS SAM and CloudFormation</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Globe className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Frontend Delivery</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Set up CloudFront CDN with S3 origin for global delivery</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented React SPA to replace legacy jQuery interface</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Integrated Cognito authentication for secure user access</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Layers className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">CI/CD Pipeline</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created AWS CodePipeline workflows for both API and UI</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Set up Git hooks for pre-merge quality checks</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented security scanning in the deployment pipeline</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Shield className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Security Implementation</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Integrated AWS Cognito for authentication and authorization</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented security scans with automated remediation tracking</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Applied least-privilege IAM roles across all services</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Architecture Diagram Placeholder */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-12">
          <h3 className="text-xl font-bold mb-4 text-gray-800">Solution Architecture</h3>
          <div className="bg-gray-100 p-4 rounded-lg h-64 flex items-center justify-center">
            <p className="text-gray-500">Architecture diagram would be displayed here</p>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="bg-gray-100 py-12">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-800">Results & Impact</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-green-100 rounded-full mr-3">
                  <PieChart className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Performance</h3>
              </div>
              <p className="text-gray-700">
                Reduced average API response time by 65% and page load times by 47% through serverless 
                architecture and CDN delivery.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-green-100 rounded-full mr-3">
                  <Code className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Maintainability</h3>
              </div>
              <p className="text-gray-700">
                Modern React codebase attracted 3 new developers to the team and reduced onboarding time 
                from 3 weeks to 1 week.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-green-100 rounded-full mr-3">
                  <Github className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">DevOps</h3>
              </div>
              <p className="text-gray-700">
                Automated CI/CD pipeline reduced deployment time from 3 hours to 15 minutes and virtually 
                eliminated production deployment failures.
              </p>
            </div>
          </div>
          
          {/* Key Learnings */}
          <div className="bg-white p-8 rounded-xl shadow-md mb-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Key Learnings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Serverless Architecture Benefits</h4>
                <p className="text-gray-700">
                  The serverless approach significantly reduced operational overhead and improved scalability. 
                  Pay-per-use model reduced costs by 40% compared to the previous always-on infrastructure.
                </p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Authentication Integration</h4>
                <p className="text-gray-700">
                  AWS Cognito provided a robust authentication system with minimal custom code, 
                  but required careful planning for integration with both frontend and backend components.
                </p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Infrastructure as Code</h4>
                <p className="text-gray-700">
                  Combining AWS SAM for Lambda functions with CloudFormation for other resources 
                  created a powerful but complex setup. Documentation was crucial for team understanding.
                </p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Frontend Modernization</h4>
                <p className="text-gray-700">
                  The React refactoring improved code quality and developer experience, but required 
                  careful planning for state management and API integration patterns.
                </p>
              </div>
            </div>
          </div>
          
          {/* Technical Implementation */}
          <div className="bg-white p-8 rounded-xl shadow-md">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Technical Implementation Highlights</h3>
            
            <div className="mb-8">
              <h4 className="font-semibold text-lg mb-3 text-gray-800">
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded mr-2">01</span>
                API Gateway & Lambda Setup
              </h4>
              <p className="text-gray-700 mb-4">
                The API architecture was built using AWS Lambda functions behind API Gateway. Each 
                function was designed for a specific business capability, following microservice principles.
              </p>
              <div className="bg-gray-50 p-4 rounded">
                <pre className="text-sm text-gray-800 overflow-x-auto">
{`# AWS SAM template example
Resources:
  GetUserFunction:
    Type: AWS::Serverless::Function
    Properties:
      Handler: handlers/getUser.handler
      Runtime: nodejs14.x
      Events:
        GetUser:
          Type: Api
          Properties:
            Path: /users/{userId}
            Method: get
            Auth:
              Authorizer: CognitoAuthorizer`}
                </pre>
              </div>
            </div>
            
            <div className="mb-8">
              <h4 className="font-semibold text-lg mb-3 text-gray-800">
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded mr-2">02</span>
                React UI Implementation
              </h4>
              <p className="text-gray-700 mb-4">
                The frontend was rebuilt using React and modern JavaScript patterns. The Cognito 
                integration was implemented using the AWS Amplify library for authentication flows.
              </p>
              <div className="bg-gray-50 p-4 rounded">
                <pre className="text-sm text-gray-800 overflow-x-auto">
{`// Cognito authentication setup example
import { Auth } from 'aws-amplify';

export const signIn = async (username, password) => {
  try {
    const user = await Auth.signIn(username, password);
    return { success: true, user };
  } catch (error) {
    console.error('Error signing in:', error);
    return { success: false, error };
  }
};`}
                </pre>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-3 text-gray-800">
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded mr-2">03</span>
                CI/CD Pipeline with Security Checks
              </h4>
              <p className="text-gray-700 mb-4">
                AWS CodePipeline was configured to automate the deployment process with built-in 
                security scanning and quality checks before deployment to production.
              </p>
              <div className="bg-gray-50 p-4 rounded">
                <pre className="text-sm text-gray-800 overflow-x-auto">
{`# Git hooks example for security scanning
#!/bin/sh
echo "Running security scan..."
npm run security-scan

if [ $? -ne 0 ]; then
  echo "Security scan failed! Please fix issues before committing."
  exit 1
fi

echo "Running unit tests..."
npm test

if [ $? -ne 0 ]; then
  echo "Tests failed! Please fix before committing."
  exit 1
fi`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Next Case Study Navigation */}
      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-xl shadow-md">
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Next Case Study
            </h3>
            <p className="text-gray-600">
              Google Partner Project for Oklahoma Modernization
            </p>
          </div>
          <a
              href="/projects/gcp-oklahoma"
              className="mt-4 md:mt-0 bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-blue-700 transition duration-300 flex items-center"
          >
            View Next Project <ArrowRight className="ml-2" size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default AwsMigrationCaseStudy;